// Funções tipadas por endpoint (§8.5 do handoff). Proibido fetch direto em views.
import type { User, MapSummary, TeamSummary, TeamRole } from '@mindlab/shared';
import type { TemplateKey } from '@mindlab/shared/templates';
import { http, request } from './http.js';

// ===== Auth =====
export const authApi = {
  request: (email: string) => http.post<void>('/auth/request', { email }),
  verify: (email: string, code: string) => http.post<{ user: User }>('/auth/verify', { email, code }),
  logout: () => http.post<void>('/auth/logout'),
  me: (signal?: AbortSignal) => http.get<{ user: User }>('/auth/me', undefined, signal),
};

// ===== Mapas =====
export type MapScope = 'all' | 'mine' | 'shared' | 'trash';

export interface MapListResponse {
  items: MapSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export const mapsApi = {
  list: (
    params: {
      scope?: MapScope;
      team?: string;
      folder?: string;
      search?: string;
      sort?: 'recent' | 'alpha';
      page?: number;
    },
    signal?: AbortSignal,
  ) => http.get<MapListResponse>('/maps', { ...params }, signal),
  create: (body: {
    title?: string;
    template?: TemplateKey;
    teamUuid?: string | null;
    folderUuid?: string | null;
  }) => http.post<{ uuid: string }>('/maps', body),
  get: (uuid: string) =>
    http.get<{ map: { uuid: string; title: string; description: string | null; isPublic: boolean; team: { uuid: string; name: string } | null; ownerName: string; updatedAt: string }; myRole: string }>(
      `/maps/${uuid}`,
    ),
  update: (
    uuid: string,
    patch: {
      title?: string;
      description?: string | null;
      isPublic?: boolean;
      teamUuid?: string | null;
      folderUuid?: string | null;
    },
  ) => http.patch<void>(`/maps/${uuid}`, patch),
  remove: (uuid: string) => http.del<void>(`/maps/${uuid}`),
  restore: (uuid: string) => http.post<void>(`/maps/${uuid}/restore`),
  purge: (uuid: string) => http.del<void>(`/maps/${uuid}/permanent`),
  duplicate: (uuid: string) => http.post<{ uuid: string }>(`/maps/${uuid}/duplicate`),
  getDocument: (uuid: string, signal?: AbortSignal) =>
    http.get<{ nodeData: unknown }>(`/maps/${uuid}/document`, undefined, signal),
  saveDocument: (uuid: string, body: { nodeData: unknown; thumbnail?: string | null }) =>
    http.put<void>(`/maps/${uuid}/document`, body),
  saveThumbnail: (uuid: string, thumbnail: string | null) =>
    http.patch<void>(`/maps/${uuid}/thumbnail`, { thumbnail }),
  upload: async (uuid: string, file: File): Promise<{ url: string; name: string; type: string }> => {
    const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${base}/maps/${uuid}/upload`, {
      method: 'POST',
      credentials: 'same-origin',
      body: form,
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
      throw new Error(err?.error?.message ?? 'Falha no upload');
    }
    return res.json();
  },
};

// ===== Equipes =====
export interface TeamMember {
  userId: number;
  name: string;
  email: string;
  role: TeamRole;
}

export const teamsApi = {
  list: () => http.get<{ teams: TeamSummary[] }>('/teams'),
  create: (name: string) => http.post<{ uuid: string }>('/teams', { name }),
  rename: (uuid: string, name: string) => http.patch<void>(`/teams/${uuid}`, { name }),
  remove: (uuid: string) => http.del<void>(`/teams/${uuid}`),
  members: (uuid: string) => http.get<{ members: TeamMember[] }>(`/teams/${uuid}/members`),
  addMember: (uuid: string, email: string, role: TeamRole) =>
    http.post<void>(`/teams/${uuid}/members`, { email, role }),
  updateMember: (uuid: string, userId: number, role: TeamRole) =>
    http.patch<void>(`/teams/${uuid}/members/${userId}`, { role }),
  removeMember: (uuid: string, userId: number) =>
    http.del<void>(`/teams/${uuid}/members/${userId}`),
};

// ===== Compartilhamento =====
export interface Collaborator {
  userId: number;
  name: string;
  email: string;
  role: 'owner' | 'editor' | 'viewer';
}
export interface ShareLink {
  id: number;
  token: string;
  role: 'editor' | 'viewer';
  url: string;
  expiresAt: string | null;
}

export const sharingApi = {
  collaborators: (uuid: string) =>
    http.get<{ collaborators: Collaborator[] }>(`/maps/${uuid}/collaborators`),
  addCollaborator: (uuid: string, email: string, role: 'editor' | 'viewer') =>
    http.post<void>(`/maps/${uuid}/collaborators`, { email, role }),
  updateCollaborator: (uuid: string, userId: number, role: 'editor' | 'viewer') =>
    http.patch<void>(`/maps/${uuid}/collaborators/${userId}`, { role }),
  removeCollaborator: (uuid: string, userId: number) =>
    http.del<void>(`/maps/${uuid}/collaborators/${userId}`),
  links: (uuid: string) => http.get<{ links: ShareLink[] }>(`/maps/${uuid}/share-links`),
  createLink: (uuid: string, role: 'editor' | 'viewer') =>
    http.post<{ link: ShareLink }>(`/maps/${uuid}/share-links`, { role }),
  revokeLink: (id: number) => http.del<void>(`/share-links/${id}`),
  resolveShared: (token: string) =>
    http.get<{
      mapUuid: string;
      title: string;
      description: string | null;
      role: 'editor' | 'viewer';
      rtTicket: string | null;
    }>(`/shared/${token}`),
  // Convidado-editor (link de edição): OTP aberto + salvar descrição via ticket.
  requestGuestCode: (token: string, email: string) =>
    http.post<void>(`/shared/${token}/request-code`, { email }),
  verifyGuestCode: (token: string, email: string, code: string) =>
    http.post<{ rtTicket: string; mapUuid: string; title: string; name: string }>(
      `/shared/${token}/verify-code`,
      { email, code },
    ),
  saveSharedDescription: (token: string, rtTicket: string, description: string | null) =>
    request<void>(`/shared/${token}/description`, {
      method: 'PATCH',
      body: { description },
      headers: { Authorization: `Bearer ${rtTicket}` },
    }),
};

// ===== Diretório (allowlist, para seletores) =====
export const directoryApi = {
  list: () => http.get<{ users: { email: string; name: string }[] }>('/directory'),
};

// ===== IA (gerar mapa a partir da descrição) =====
export const ingestApi = {
  get: (uuid: string) => http.get<{ enabled: boolean; token: string | null }>(`/maps/${uuid}/ingest`),
  generateToken: (uuid: string) => http.post<{ token: string }>(`/maps/${uuid}/ingest/token`),
  revokeToken: (uuid: string) => http.del<void>(`/maps/${uuid}/ingest/token`),
};

export const aiApi = {
  generateMap: (uuid: string, description: string) =>
    http.post<{ nodeData: unknown }>(`/maps/${uuid}/generate`, { description }),
  analyzeMap: (uuid: string, content: string) =>
    http.post<{ decisions: unknown; readiness: { score: number; label: string; summary: string } }>(
      `/maps/${uuid}/analyze`,
      { content },
    ),
};

// ===== Blocos (biblioteca) =====
export interface BlockSummary {
  uuid: string;
  name: string;
  description: string | null;
  ownerName: string;
  ownerEmail: string;
  updatedAt: string;
}

export const blocksApi = {
  list: () => http.get<{ blocks: BlockSummary[] }>('/blocks'),
  create: (body: { name: string; description?: string | null; nodeData: unknown }) =>
    http.post<{ uuid: string }>('/blocks', body),
  get: (uuid: string) =>
    http.get<{ name: string; description: string | null; nodeData: unknown }>(`/blocks/${uuid}`),
  update: (uuid: string, patch: { name?: string; description?: string | null }) =>
    http.patch<void>(`/blocks/${uuid}`, patch),
  remove: (uuid: string) => http.del<void>(`/blocks/${uuid}`),
};

// ===== Web Push =====
export const pushApi = {
  subscribe: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    http.post<void>('/push/subscribe', subscription),
  unsubscribe: (endpoint: string) => http.post<void>('/push/unsubscribe', { endpoint }),
};

// ===== Histórico de versões =====
export interface MapVersion {
  id: number;
  createdAt: string;
}

export const versionsApi = {
  list: (uuid: string) => http.get<{ versions: MapVersion[] }>(`/maps/${uuid}/versions`),
  get: (uuid: string, id: number) => http.get<{ nodeData: unknown }>(`/maps/${uuid}/versions/${id}`),
};

// ===== Comentários por nó =====
export interface NodeComment {
  id: number;
  nodeId: string;
  body: string;
  createdAt: string;
  authorName: string;
  authorEmail: string;
  authorColor: string;
  authorId: number;
}

export const commentsApi = {
  counts: (uuid: string) => http.get<{ counts: Record<string, number> }>(`/maps/${uuid}/comments/counts`),
  list: (uuid: string, nodeId: string) =>
    http.get<{ comments: NodeComment[] }>(`/maps/${uuid}/comments`, { nodeId }),
  add: (uuid: string, nodeId: string, body: string) =>
    http.post<void>(`/maps/${uuid}/comments`, { nodeId, body }),
  remove: (uuid: string, id: number) => http.del<void>(`/maps/${uuid}/comments/${id}`),
};

// ===== Pastas (projetos) =====
export interface FolderSummary {
  uuid: string;
  name: string;
  mapCount: number;
}

export const foldersApi = {
  list: () => http.get<{ folders: FolderSummary[] }>('/folders'),
  create: (name: string) => http.post<{ uuid: string }>('/folders', { name }),
  rename: (uuid: string, name: string) => http.patch<void>(`/folders/${uuid}`, { name }),
  remove: (uuid: string) => http.del<void>(`/folders/${uuid}`),
};

// ===== Usuários (allowlist, admin) =====
export interface AllowlistUser {
  email: string;
  name: string;
  role: 'admin' | 'user';
  protected: boolean;
}

export const usersApi = {
  list: () => http.get<{ users: AllowlistUser[]; roles: string[] }>('/users'),
  create: (body: { email: string; name: string; role: 'admin' | 'user' }) =>
    http.post<void>('/users', body),
  update: (email: string, body: { name: string; role: 'admin' | 'user' }) =>
    http.patch<void>(`/users/${encodeURIComponent(email)}`, body),
  remove: (email: string) => http.del<void>(`/users/${encodeURIComponent(email)}`),
};
